/**
 * Ember Sentry Adapter
 *
 * This adapter bridges the Ember Sentry service to the framework-agnostic
 * Sentry interface from @repo/shared-kit, allowing React components in
 * Ember apps to use Sentry without depending on @sentry/ember directly.
 */

import type { Sentry, SentryScope } from '@repo/shared-kit/sentry';

import type SentryServiceType from 'register-partners-js/services/sentry';

export function createEmberSentryAdapter(sentryService: SentryServiceType): Sentry {
  return {
    captureException(error: unknown, options): void {
      sentryService.captureException(error, options?.captureContext);
    },

    captureMessage(message: string, options): void {
      sentryService.captureMessage(message, options?.level || 'info');
    },

    withScope(callback: (scope: SentryScope) => void): void {
      sentryService.withScope(emberScope => {
        // Adapt the Ember Sentry scope to our SentryScope interface
        const adaptedScope: SentryScope = {
          setTag: (key: string, value: string | undefined) => {
            emberScope.setTag(key, value);
          },
          setTags: (tags: Record<string, string | undefined>) => {
            emberScope.setTags(tags);
          },
          setContext: (key: string, context: Record<string, unknown> | null) => {
            emberScope.setContext(key, context);
          },
          setExtra: (key: string, extra: unknown) => {
            emberScope.setExtra(key, extra);
          },
          setLevel: level => {
            emberScope.setLevel(level);
          },
        };

        callback(adaptedScope);
      });
    },

    setTagInCurrentScope(tag: string, value: string | undefined): void {
      // Note: register-partners-js SentryService doesn't have setTagInCurrentScope
      // We implement it using withScope as a fallback
      sentryService.withScope(scope => {
        scope.setTag(tag, value);
      });
    },
  };
}
